import { useEffect, useMemo, useRef } from "react";
import { campaignOverviewService } from "../services/campaignOverviewService";
import useInfiniteFetcher from "@/helpers/hooks/useInfiniteFetcher";
import useAutoLoadUntilScrollable from "@/helpers/hooks/useAutoLoadUntilScrollable";
import { sanitizeSearchInput } from "@/helpers/utils";

const PAGE_SIZE = 12;
const CONTAINER_ID = "campaign-overview-digital";
const DEFAULT_ERROR_MESSAGE =
  "Unable to load digital campaigns. Please try again.";

type ParticipantFilterValue = {
  id: string | number;
  systemId?: string | number;
};

type CampaignOverviewFilters = {
  search?: string;
  tags?: Array<any>;
  channels?: Array<any>;
  groups?: Array<string | number>;
  participant?: ParticipantFilterValue | null;
};

type CampaignOverviewHookParams = {
  filters: CampaignOverviewFilters;
  selectedBrand?: { id?: string | number } | null;
  loggedInUser?: any;
  isParticipantsLoading?: boolean;
};

function useDigitalCampaignOverview({
  filters,
  selectedBrand,
  loggedInUser,
  isParticipantsLoading = false,
}: CampaignOverviewHookParams) {
  const refreshTokenRef = useRef(0);
  const prevFiltersRef = useRef<string | null>(null);
  const serializedFilters = JSON.stringify(filters);
  if (prevFiltersRef.current !== serializedFilters) {
    prevFiltersRef.current = serializedFilters;
    refreshTokenRef.current += 1;
  }

  const hasParticipant = !!filters.participant;

  const reqData = useMemo(
    () =>
      hasParticipant
        ? {
            _r: refreshTokenRef.current,
            id: selectedBrand?.id,
            fk_participant_id: filters.participant.id,
            participant_id: filters.participant.systemId,
            userId: loggedInUser?.participantData?.id,
            offset: 0,
            limit: PAGE_SIZE,
            ...(filters.search && {
              campaign_name: sanitizeSearchInput(filters.search),
            }),
            ...(filters.tags?.length > 0 && {
              tag_id: filters.tags.map((t) => t.id).join(","),
            }),
            ...(filters.channels?.length > 0 && {
              channel_id: filters.channels.map((c) => c.id).join(","),
            }),
            ...(filters.groups?.length > 0 && {
              group_id: filters.groups.join(","),
            }),
          }
        : null,
    [selectedBrand?.id, loggedInUser, filters, hasParticipant]
  );

  const { data, error, isLoading, isValidating, size, setSize } =
    useInfiniteFetcher(reqData, campaignOverviewService.getDigitalCampaigns, {
      cacheKeyPrefix: "campaign-overview-digital",
      revalidateOnFocus: false,
      revalidateFirstPage: true,
    });

  const campaignPages = data as any[] | undefined;
  const campaigns =
    campaignPages?.flatMap((d) => d?.campaignTabsData?.data || []) ?? [];
  const isLastPage =
    size >=
    campaignPages?.[campaignPages?.length - 1]?.campaignTabsData?.pageCount;
  const hasMore = !isLastPage;

  const loadingRef = useRef(false);

  useAutoLoadUntilScrollable({
    containerId: CONTAINER_ID,
    isLastPage,
    hasError: !!error,
    isValidating,
    dependency: campaigns.length,
    triggerLoad: () => setSize((prev: number) => prev + 1),
  });

  useEffect(() => {
    loadingRef.current = isValidating;
  }, [isValidating]);

  useEffect(() => {
    const handleScroll = () => {
      const scrolledTo = window.scrollY + window.innerHeight;
      const isNearBottom = document.body.scrollHeight - 400 <= scrolledTo;
      if (isNearBottom && !isLastPage && !loadingRef.current && !error) {
        loadingRef.current = true;
        setSize((prev: number) => prev + 1);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isLastPage, error, setSize]);

  return {
    campaigns,
    isLoading:
      isParticipantsLoading ||
      (hasParticipant && !error && (isLoading || isValidating || !data)),
    isValidating,
    error,
    errorMessage: error ? DEFAULT_ERROR_MESSAGE : "",
    hasMore,
    containerId: CONTAINER_ID,
  };
}

export default useDigitalCampaignOverview;
